import { Grid, Stack } from "@mui/material";
import { MprcMessageBox, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { MprcButton } from "components/MprcButton";
import { StandardAccountConfirmResponse, StandardAccountConfirmStatus, StandardAccountResendConfirmationResponse, StandardAccountResendConfirmationStatus } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { RootPaths } from "routes/RootRouter";

////////////////////////////////////////////////////////////////////////////////////////

const AccountAlreadyConfirmed: React.FC = () => {
    const { t } = useTranslation();

    return <MprcMessageBox
        variant="error"
        size="large"
        message={
            <Stack>
                <div>{t("Auth.Common.AlreadyConfirmedMessage")}</div>
                <div>{t("Auth.Common.CallAdminText")}</div>
            </Stack>
        }
    />;
};

////////////////////////////////////////////////////////////////////////////////////////

const AccountNotFound: React.FC = () => {
    const { t } = useTranslation();

    return <MprcMessageBox
        variant="error"
        size="large"
        message={
            <Stack>
                <div>{t("Auth.Common.AccountNotFoundMessage")}</div>
                <div>{t("Auth.Common.CallAdminText")}</div>
            </Stack>
        }
    />;
};

////////////////////////////////////////////////////////////////////////////////////////

type RouteParams = {
    securityStamp: string;
};

////////////////////////////////////////////////////////////////////////////////////////

export const AccountConfirmation: React.FC = () => {

    const [submiting, setSubmiting] = useState<boolean>(false);
    const [confirmationResult, setConfirmationResult] = useState<StandardAccountConfirmResponse | null>(null);
    const [resendConfirmationResult, setResendConfirmationResult] = useState<StandardAccountResendConfirmationResponse | null>(null);
    const [viewError, setViewError] = useState<any>();
    const { securityStamp } = useParams<RouteParams>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const api = useCoreApi();

    ///
    const executeConfirmation = () => {

        setSubmiting(true);
        setConfirmationResult(null);
        setResendConfirmationResult(null);

        api.v1.account.standardAccountConfirm({ securityStamp: securityStamp! })
            .then((response) => {
                setConfirmationResult(response.data);
                setSubmiting(false);
            })
            .catch((error: any) => {
                setSubmiting(false);
                setViewError(error);
            });
    };

    ///
    const resendConfirmation = () => {

        setSubmiting(true);
        setConfirmationResult(null);
        setResendConfirmationResult(null);

        api.v1.account.standardAccountResendConfirmation({ securityStamp: securityStamp!, authFieldValue: null })
            .then((response) => {
                setResendConfirmationResult(response.data);
                setSubmiting(false);
            })
            .catch((error: any) => {
                setSubmiting(false);
                setViewError(error);
            });
    };

    ///
    const renderEmailNotSend = () => {
        return <MprcMessageBox
            variant="error"
            size="large"
            message={
                <Stack>
                    <div>{t("Auth.AccountConfirmation.EmailNotSend")}</div>
                    <div>{t("Auth.Common.CallAdminText")}</div>
                </Stack>
            }
        />;
    };

    ///
    const renderExpired = () => {
        return <MprcMessageBox
            variant="error"
            size="large"
            message={t("Auth.AccountConfirmation.ExpiredMessage")}
            action={
                <Stack direction="row" spacing={1} >
                    <MprcButton
                        variant="contained"
                        color="error"
                        fullWidth
                        onClick={() => resendConfirmation()}
                    >
                        <span>{t("Auth.AccountConfirmation.ConfirmationButton")}</span>
                    </MprcButton>

                    <MprcButton
                        variant="contained"
                        color="error"
                        fullWidth
                        onClick={() => resendConfirmation()}
                    >
                        <span>{t("Auth.Common.LoginButton")}</span>
                    </MprcButton>
                </Stack>
            }
        />;
    };

    ///
    const renderResendConfirmation = () => {
        if (resendConfirmationResult?.status == StandardAccountResendConfirmationStatus.NotFound)
            return <AccountNotFound />;
        if (resendConfirmationResult?.status == StandardAccountResendConfirmationStatus.AlreadyConfirmed)
            return <AccountAlreadyConfirmed />;

        if (resendConfirmationResult?.status == StandardAccountResendConfirmationStatus.EmailNotSend)
            return renderEmailNotSend();

        return (
            <MprcMessageBox
                variant="info"
                size="large"
                message={t("Auth.AccountConfirmation.ReconfirmationDoneMessage")}
                action={
                    <MprcButton
                        color="info"
                        size="large"
                        onClick={() => navigate(RootPaths.Login, { replace: true })}
                    >
                        <span>{t("Auth.Common.LoginButton")}</span>
                    </MprcButton>
                }
            />
        );
    };


    ///
    const renderConfirmation = () => {

        if (confirmationResult!.status == StandardAccountConfirmStatus.NotFound)
            return <AccountNotFound />;
        if (confirmationResult!.status == StandardAccountConfirmStatus.AlreadyConfirmed)
            return <AccountAlreadyConfirmed />;

        if (confirmationResult!.status == StandardAccountConfirmStatus.Expired)
            return renderExpired();

        return <MprcMessageBox
            variant="success"
            size="large"
            message={t("Auth.AccountConfirmation.ConfirmedMessage")}
            action={
                <MprcButton
                    color="info"
                    size="large"
                    onClick={() => navigate(RootPaths.Login, { replace: true })}
                >
                    <span>{t("Auth.Common.LoginButton")}</span>
                </MprcButton>
            }
        />;
    };

    ///
    useEffect(() => {
        executeConfirmation();
    }, []);

    return (
        <>
            {submiting && <MprcRequestIndicator type="waiting" />}

            <Grid container justifyContent="center" alignItems="center" spacing={1}>
                <Grid size={{ xs: 6 }}>
                    {
                        viewError != null
                            ? <MprcRequestErrorReport error={viewError} />
                            : resendConfirmationResult != null
                                ? renderResendConfirmation()
                                : renderConfirmation()
                    }
                </Grid>
            </Grid>
        </>
    );
};
